// UserManagement.jsx – Admin User Management Page
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Pagination,
  Skeleton,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as VisibilityIcon,
  ManageAccounts as ManagerIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import Header from "../components/Header";
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  setUserPassword,
  toggleUserActive,
  fetchUserStats,
} from "../api/userManagementApi";
import "./UserManagement.css";

// ─── Styled Components ─────────────────────────────────────────────────────

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
  border: "1px solid #e8eceb",
  overflow: "hidden",
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: "#f4f7f6",
  "& .MuiTableCell-root": {
    fontFamily: '"Yekan Bakh FaNum Bold", sans-serif',
    fontWeight: 600,
    color: "#2a4a37",
    fontSize: "0.9rem",
    padding: "16px",
    borderBottom: "2px solid #e3ede7",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#f8faf9",
  },
  "& .MuiTableCell-root": {
    fontFamily: '"Yekan Bakh FaNum Medium", sans-serif',
    padding: "14px 16px",
    borderBottom: "1px solid #f0f3f2",
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontFamily: '"Yekan Bakh FaNum Medium", sans-serif',
  fontSize: "0.8rem",
  height: "28px",
  borderRadius: "14px",
  backgroundColor: status === "active" ? "#e8f5e9" : "#ffebee",
  color: status === "active" ? "#2e7d32" : "#c62828",
  border: `1px solid ${status === "active" ? "#a5d6a7" : "#ef9a9a"}`,
  "& .MuiChip-label": {
    padding: "0 10px",
  },
}));

const AccessLevelChip = styled(Chip)(({ theme, level }) => {
  const colors = {
    admin: { bg: "#fce4ec", color: "#c2185b", border: "#f8bbd9" },
    premium: { bg: "#e3f2fd", color: "#1565c0", border: "#bbdefb" },
    standard: { bg: "#e8f5e9", color: "#2e7d32", border: "#c8e6c9" },
    basic: { bg: "#f5f5f5", color: "#616161", border: "#e0e0e0" },
  };
  const c = colors[level] || colors.basic;
  return {
    fontFamily: '"Yekan Bakh FaNum Medium", sans-serif',
    fontSize: "0.8rem",
    height: "28px",
    borderRadius: "14px",
    backgroundColor: c.bg,
    color: c.color,
    border: `1px solid ${c.border}`,
    "& .MuiChip-label": {
      padding: "0 10px",
    },
  };
});

const ActionButton = styled(IconButton)(({ theme, color }) => ({
  width: "36px",
  height: "36px",
  borderRadius: "10px",
  margin: "0 3px",
  transition: "all 0.2s ease",
  backgroundColor:
    color === "edit" ? "#e3f2fd" : color === "password" ? "#fff3e0" : "#ffebee",
  color:
    color === "edit" ? "#1565c0" : color === "password" ? "#e65100" : "#c62828",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    backgroundColor:
      color === "edit"
        ? "#bbdefb"
        : color === "password"
          ? "#ffe0b2"
          : "#ffcdd2",
  },
}));

const StatsCard = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #2a4a37 0%, #3d6b4f 100%)",
  borderRadius: "16px",
  padding: "20px 24px",
  color: "white",
  display: "flex",
  alignItems: "center",
  gap: "16px",
  boxShadow: "0 8px 24px rgba(42, 74, 55, 0.25)",
}));

// ─── Helper Functions ────────────────────────────────────────────────────────

const getAccessLevelLabel = (level) => {
  const labels = {
    admin: "مدیر کل",
    premium: "ویژه",
    standard: "استاندارد",
    basic: "پایه",
  };
  return labels[level] || level;
};

const getAccessLevelIcon = (level) => {
  switch (level) {
    case "admin":
      return <AdminIcon />;
    case "premium":
      return <VisibilityIcon />;
    case "standard":
      return <ManagerIcon />;
    default:
      return <PersonIcon />;
  }
};

// ─── Main Component ──────────────────────────────────────────────────────────

const UserManagement = ({ onLogout }) => {
  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAccessLevel, setFilterAccessLevel] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  // Modal states
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Form states
  const [formData, setFormData] = useState({
    username: "",
    full_name: "",
    email: "",
    password: "",
    access_level: "basic",
    is_active: true,
  });
  const [passwordForm, setPasswordForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const pageSize = 10;

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    loadUsers();
    loadStats();
  }, [page, searchQuery, filterAccessLevel, filterStatus]);

  // ─── Data Loading ──────────────────────────────────────────────────────────

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        page_size: pageSize,
        search: searchQuery || undefined,
        access_level: filterAccessLevel || undefined,
        is_active: filterStatus !== "" ? filterStatus === "active" : undefined,
      };
      const response = await fetchUsers(params);
      setUsers(response.results || []);
      setTotalCount(response.count || 0);
    } catch (err) {
      setError(err.message || "خطا در بارگذاری کاربران");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await fetchUserStats();
      setStats(data);
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  };

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const handleFilterAccessLevel = (e) => {
    setFilterAccessLevel(e.target.value);
    setPage(1);
  };

  const handleFilterStatus = (e) => {
    setFilterStatus(e.target.value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setFilterAccessLevel("");
    setFilterStatus("");
    setPage(1);
  };

  // ─── Modal Handlers ────────────────────────────────────────────────────────

  const openAddModal = () => {
    setEditingUser(null);
    setFormData({
      username: "",
      full_name: "",
      email: "",
      password: "",
      access_level: "basic",
      is_active: true,
    });
    setIsUserModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username || "",
      full_name: user.full_name || "",
      email: user.email || "",
      password: "",
      access_level: user.access_level || "basic",
      is_active: user.is_active !== false,
    });
    setIsUserModalOpen(true);
  };

  const openPasswordModal = (user) => {
    setPasswordUser(user);
    setPasswordForm({ newPassword: "", confirmPassword: "" });
    setIsPasswordModalOpen(true);
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  // ─── Form Submission ───────────────────────────────────────────────────────

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        await updateUser(editingUser.id, formData);
        setSnackbar({
          open: true,
          message: "کاربر با موفقیت ویرایش شد",
          severity: "success",
        });
      } else {
        await createUser(formData);
        setSnackbar({
          open: true,
          message: "کاربر جدید با موفقیت ایجاد شد",
          severity: "success",
        });
      }
      setIsUserModalOpen(false);
      loadUsers();
      loadStats();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "خطا در ذخیره کاربر",
        severity: "error",
      });
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setSnackbar({
        open: true,
        message: "رمز عبور و تکرار آن مطابقت ندارند",
        severity: "error",
      });
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setSnackbar({
        open: true,
        message: "رمز عبور باید حداقل ۶ کاراکتر باشد",
        severity: "error",
      });
      return;
    }
    try {
      await setUserPassword(passwordUser.id, passwordForm.newPassword);
      setSnackbar({
        open: true,
        message: "رمز عبور با موفقیت تغییر یافت",
        severity: "success",
      });
      setIsPasswordModalOpen(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "خطا در تغییر رمز عبور",
        severity: "error",
      });
    }
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUser(userToDelete.id);
      setSnackbar({
        open: true,
        message: "کاربر با موفقیت حذف شد",
        severity: "success",
      });
      setIsDeleteDialogOpen(false);
      loadUsers();
      loadStats();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "خطا در حذف کاربر",
        severity: "error",
      });
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await toggleUserActive(user.id);
      setSnackbar({
        open: true,
        message: `کاربر ${user.is_active ? "غیرفعال" : "فعال"} شد`,
        severity: "success",
      });
      loadUsers();
      loadStats();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "خطا در تغییر وضعیت",
        severity: "error",
      });
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="user-management-wrapper">
      <Header onLogout={onLogout} />
      <div className="user-management-page">
        <div className="user-management-container">
          {/* Clean Header Section */}
          <div className="user-management-header">
            <div className="header-title">
              <AdminIcon sx={{ fontSize: 28, color: "#2a4a37", mr: 1 }} />
              <Typography variant="h5" className="page-title">
                مدیریت کاربران
              </Typography>
            </div>
            <Button
              variant="contained"
              className="add-user-btn"
              onClick={openAddModal}
              startIcon={<AddIcon />}
            >
              افزودن کاربر
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="stats-row">
            <div className="stat-card total">
              <div className="stat-icon">
                <PersonIcon />
              </div>
              <div className="stat-content">
                <Typography className="stat-value">
                  {stats.total || 0}
                </Typography>
                <Typography className="stat-label">کل کاربران</Typography>
              </div>
            </div>
            <div className="stat-card active">
              <div className="stat-icon">
                <VisibilityIcon />
              </div>
              <div className="stat-content">
                <Typography className="stat-value">
                  {stats.active || 0}
                </Typography>
                <Typography className="stat-label">فعال</Typography>
              </div>
            </div>
            <div className="stat-card inactive">
              <div className="stat-icon">
                <LockIcon />
              </div>
              <div className="stat-content">
                <Typography className="stat-value">
                  {stats.inactive || 0}
                </Typography>
                <Typography className="stat-label">غیرفعال</Typography>
              </div>
            </div>
          </div>

          {/* Filters and Actions */}
          <Paper className="filters-paper" elevation={0}>
            <div className="filters-row">
              <TextField
                className="search-field"
                placeholder="جستجو در نام کاربری، نام کامل یا ایمیل..."
                value={searchQuery}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#8a9a8f" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <div className="filter-selects">
                <FormControl className="filter-select" size="small">
                  <InputLabel>سطح دسترسی</InputLabel>
                  <Select
                    value={filterAccessLevel}
                    onChange={handleFilterAccessLevel}
                    label="سطح دسترسی"
                  >
                    <MenuItem value="">همه</MenuItem>
                    <MenuItem value="admin">مدیر کل</MenuItem>
                    <MenuItem value="premium">ویژه</MenuItem>
                    <MenuItem value="standard">استاندارد</MenuItem>
                    <MenuItem value="basic">پایه</MenuItem>
                  </Select>
                </FormControl>
                <FormControl className="filter-select" size="small">
                  <InputLabel>وضعیت</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={handleFilterStatus}
                    label="وضعیت"
                  >
                    <MenuItem value="">همه</MenuItem>
                    <MenuItem value="active">فعال</MenuItem>
                    <MenuItem value="inactive">غیرفعال</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <Tooltip title="به‌روزرسانی">
                <IconButton onClick={loadUsers} className="refresh-btn">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </div>
          </Paper>

          {/* Add User Button */}
          <div className="actions-row">
            <Button
              variant="contained"
              className="add-user-btn"
              onClick={openAddModal}
              startIcon={<AddIcon />}
            >
              افزودن کاربر جدید
            </Button>
            <Typography className="total-count">
              تعداد کل: <strong>{totalCount}</strong> کاربر
            </Typography>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              className="error-alert"
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Users Table */}
          <StyledTableContainer component={Paper} elevation={0}>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell align="center">ردیف</TableCell>
                  <TableCell>نام کاربری</TableCell>
                  <TableCell>نام کامل</TableCell>
                  <TableCell>ایمیل</TableCell>
                  <TableCell align="center">سطح دسترسی</TableCell>
                  <TableCell align="center">وضعیت</TableCell>
                  <TableCell align="center">تاریخ عضویت</TableCell>
                  <TableCell align="center">عملیات</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 8 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" width="80%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                      className="empty-cell"
                    >
                      <Box className="empty-state">
                        <PersonIcon sx={{ fontSize: 64, color: "#c5d1c8" }} />
                        <Typography variant="h6" className="empty-title">
                          هیچ کاربری یافت نشد
                        </Typography>
                        <Typography variant="body2" className="empty-subtitle">
                          {searchQuery || filterAccessLevel || filterStatus
                            ? "فیلترهای اعمال شده را بررسی کنید"
                            : "برای شروع، یک کاربر جدید اضافه کنید"}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user, index) => (
                    <StyledTableRow key={user.id}>
                      <TableCell align="center">
                        {(page - 1) * pageSize + index + 1}
                      </TableCell>
                      <TableCell>
                        <Typography className="username-cell">
                          {user.username}
                        </Typography>
                      </TableCell>
                      <TableCell>{user.full_name}</TableCell>
                      <TableCell>{user.email || "-"}</TableCell>
                      <TableCell align="center">
                        <AccessLevelChip
                          level={user.access_level}
                          icon={getAccessLevelIcon(user.access_level)}
                          label={getAccessLevelLabel(user.access_level)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            user.is_active
                              ? "کلیک برای غیرفعال کردن"
                              : "کلیک برای فعال کردن"
                          }
                        >
                          <Box
                            onClick={() => handleToggleStatus(user)}
                            style={{ cursor: "pointer" }}
                          >
                            <StatusChip
                              status={user.is_active ? "active" : "inactive"}
                              label={user.is_active ? "فعال" : "غیرفعال"}
                              size="small"
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" className="date-cell">
                        {user.date_joined || "-"}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="ویرایش">
                          <ActionButton
                            color="edit"
                            onClick={() => openEditModal(user)}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </ActionButton>
                        </Tooltip>
                        <Tooltip title="تغییر رمز عبور">
                          <ActionButton
                            color="password"
                            onClick={() => openPasswordModal(user)}
                            size="small"
                          >
                            <LockIcon fontSize="small" />
                          </ActionButton>
                        </Tooltip>
                        <Tooltip title="حذف">
                          <ActionButton
                            color="delete"
                            onClick={() => openDeleteDialog(user)}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </ActionButton>
                        </Tooltip>
                      </TableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>

          {/* Pagination */}
          {totalCount > 0 && (
            <Box className="pagination-container">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                shape="rounded"
                size="large"
                showFirstButton
                showLastButton
              />
              <Typography className="pagination-info">
                نمایش {(page - 1) * pageSize + 1} تا{" "}
                {Math.min(page * pageSize, totalCount)} از {totalCount} کاربر
              </Typography>
            </Box>
          )}
        </div>

        {/* Add/Edit User Modal */}
        <Dialog
          open={isUserModalOpen}
          onClose={() => setIsUserModalOpen(false)}
          maxWidth="sm"
          fullWidth
          className="user-modal"
        >
          <DialogTitle className="modal-title">
            {editingUser ? "ویرایش کاربر" : "افزودن کاربر جدید"}
          </DialogTitle>
          <DialogContent>
            <Box className="form-grid">
              <TextField
                name="username"
                label="نام کاربری"
                value={formData.username}
                onChange={handleFormChange}
                fullWidth
                required
                disabled={!!editingUser}
                className="form-field"
              />
              <TextField
                name="full_name"
                label="نام کامل"
                value={formData.full_name}
                onChange={handleFormChange}
                fullWidth
                required
                className="form-field"
              />
              <TextField
                name="email"
                label="ایمیل"
                type="email"
                value={formData.email}
                onChange={handleFormChange}
                fullWidth
                className="form-field span-2"
              />
              {!editingUser && (
                <TextField
                  name="password"
                  label="رمز عبور"
                  type="password"
                  value={formData.password}
                  onChange={handleFormChange}
                  fullWidth
                  required={!editingUser}
                  className="form-field span-2"
                />
              )}
              <FormControl fullWidth className="form-field">
                <InputLabel>سطح دسترسی</InputLabel>
                <Select
                  name="access_level"
                  value={formData.access_level}
                  onChange={handleFormChange}
                  label="سطح دسترسی"
                >
                  <MenuItem value="admin">مدیر کل</MenuItem>
                  <MenuItem value="premium">ویژه</MenuItem>
                  <MenuItem value="standard">استاندارد</MenuItem>
                  <MenuItem value="basic">پایه</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth className="form-field">
                <InputLabel>وضعیت</InputLabel>
                <Select
                  name="is_active"
                  value={formData.is_active}
                  onChange={handleFormChange}
                  label="وضعیت"
                >
                  <MenuItem value={true}>فعال</MenuItem>
                  <MenuItem value={false}>غیرفعال</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions className="modal-actions">
            <Button
              onClick={() => setIsUserModalOpen(false)}
              className="cancel-btn"
            >
              انصراف
            </Button>
            <Button
              onClick={handleSaveUser}
              variant="contained"
              className="save-btn"
              disabled={
                !formData.username ||
                !formData.full_name ||
                (!editingUser && !formData.password)
              }
            >
              {editingUser ? "ذخیره تغییرات" : "ایجاد کاربر"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Change Password Modal */}
        <Dialog
          open={isPasswordModalOpen}
          onClose={() => setIsPasswordModalOpen(false)}
          maxWidth="xs"
          fullWidth
          className="password-modal"
        >
          <DialogTitle className="modal-title">تغییر رمز عبور</DialogTitle>
          <DialogContent>
            {passwordUser && (
              <Typography className="password-username">
                کاربر: <strong>{passwordUser.username}</strong>
              </Typography>
            )}
            <TextField
              name="newPassword"
              label="رمز عبور جدید"
              type="password"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              fullWidth
              required
              className="form-field"
            />
            <TextField
              name="confirmPassword"
              label="تکرار رمز عبور"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              fullWidth
              required
              className="form-field"
            />
          </DialogContent>
          <DialogActions className="modal-actions">
            <Button
              onClick={() => setIsPasswordModalOpen(false)}
              className="cancel-btn"
            >
              انصراف
            </Button>
            <Button
              onClick={handleChangePassword}
              variant="contained"
              className="save-btn"
              disabled={
                !passwordForm.newPassword || !passwordForm.confirmPassword
              }
            >
              تغییر رمز
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          maxWidth="xs"
          className="delete-modal"
        >
          <DialogTitle className="modal-title delete-title">
            حذف کاربر
          </DialogTitle>
          <DialogContent>
            <Typography className="delete-message">
              آیا از حذف کاربر <strong>{userToDelete?.username}</strong> اطمینان
              دارید؟
            </Typography>
            <Typography className="delete-warning">
              این عملیات غیرقابل بازگشت است.
            </Typography>
          </DialogContent>
          <DialogActions className="modal-actions">
            <Button
              onClick={() => setIsDeleteDialogOpen(false)}
              className="cancel-btn"
            >
              انصراف
            </Button>
            <Button
              onClick={handleDeleteUser}
              variant="contained"
              className="delete-btn"
            >
              حذف کاربر
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default UserManagement;
